import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Plus, Trash2, Eye, Code, FileText, X, AlertCircle } from 'lucide-react';

const APIBuilder = ({ api, onSave, onCancel, isLoading }) => {
  const [apiData, setApiData] = useState(api || {
    name: '',
    description: '',
    category: '',
    base_url: '',
    docs_url: '',
    version: '1.0.0',
    is_active: true,
    endpoints: []
  });

  const [currentEndpoint, setCurrentEndpoint] = useState({
    method: 'GET',
    path: '',
    summary: '',
    description: '',
    parameters: [],
    request_example: '',
    response_example: '',
    error_codes: ''
  });

  const [editingEndpointIndex, setEditingEndpointIndex] = useState(null);
  const [showEndpointEditor, setShowEndpointEditor] = useState(false);

  // Parameter state
  const [currentParam, setCurrentParam] = useState({
    name: '',
    type: 'body',
    data_type: 'string',
    required: false,
    description: '',
    example: ''
  });

  const handleAddParameter = () => {
    if (!currentParam.name) return;
    
    setCurrentEndpoint({
      ...currentEndpoint,
      parameters: [...currentEndpoint.parameters, { ...currentParam, id: String(Date.now()) }]
    });
    
    setCurrentParam({
      name: '',
      type: 'body',
      data_type: 'string',
      required: false,
      description: '',
      example: ''
    });
  };

  const handleRemoveParameter = (paramId) => {
    setCurrentEndpoint({
      ...currentEndpoint,
      parameters: currentEndpoint.parameters.filter(p => p.id !== paramId)
    });
  };

  const handleAddEndpoint = () => {
    if (!currentEndpoint.path || !currentEndpoint.summary) return;

    if (editingEndpointIndex !== null) {
      // Update existing endpoint
      const updatedEndpoints = [...apiData.endpoints];
      updatedEndpoints[editingEndpointIndex] = { ...currentEndpoint };
      setApiData({ ...apiData, endpoints: updatedEndpoints });
      setEditingEndpointIndex(null);
    } else {
      // Add new endpoint
      setApiData({
        ...apiData,
        endpoints: [...apiData.endpoints, { ...currentEndpoint, id: String(Date.now()) }]
      });
    }

    // Reset form
    setCurrentEndpoint({
      method: 'GET',
      path: '',
      summary: '',
      description: '',
      parameters: [],
      request_example: '',
      response_example: '',
      error_codes: ''
    });
    setShowEndpointEditor(false);
  };

  const handleEditEndpoint = (index) => {
    setCurrentEndpoint(apiData.endpoints[index]);
    setEditingEndpointIndex(index);
    setShowEndpointEditor(true);
  };

  const handleRemoveEndpoint = (index) => {
    setApiData({
      ...apiData,
      endpoints: apiData.endpoints.filter((_, i) => i !== index)
    });
  };

  const handleSaveAPI = () => {
    onSave(apiData);
  };

  return (
    <div className="space-y-6">
      {/* API Basic Info */}
      <Card>
        <CardHeader>
          <CardTitle>Informazioni API</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Nome API *</label>
                <Input
                  value={apiData.name}
                  onChange={(e) => setApiData({ ...apiData, name: e.target.value })}
                  placeholder="es. AddressCheck"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Categoria *</label>
                <Input
                  value={apiData.category}
                  onChange={(e) => setApiData({ ...apiData, category: e.target.value })}
                  placeholder="es. Validazione Indirizzi"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Descrizione *</label>
              <textarea
                className="w-full p-2 border rounded-md"
                rows={3}
                value={apiData.description}
                onChange={(e) => setApiData({ ...apiData, description: e.target.value })}
                placeholder="Descrizione completa dell'API..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Base URL</label>
                <Input
                  value={apiData.base_url}
                  onChange={(e) => setApiData({ ...apiData, base_url: e.target.value })}
                  placeholder="https://api.example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Versione *</label>
                <Input
                  value={apiData.version}
                  onChange={(e) => setApiData({ ...apiData, version: e.target.value })}
                  placeholder="1.0.0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">URL Docs</label>
                <Input
                  value={apiData.docs_url}
                  onChange={(e) => setApiData({ ...apiData, docs_url: e.target.value })}
                  placeholder="/developers"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="api_active"
                checked={apiData.is_active}
                onChange={(e) => setApiData({ ...apiData, is_active: e.target.checked })}
              />
              <label htmlFor="api_active" className="text-sm font-medium">
                API Attiva
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Endpoints Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Endpoints API ({apiData.endpoints.length})</CardTitle>
          <Button
            onClick={() => {
              setShowEndpointEditor(true);
              setEditingEndpointIndex(null);
              setCurrentEndpoint({
                method: 'GET',
                path: '',
                summary: '',
                description: '',
                parameters: [],
                request_example: '',
                response_example: '',
                error_codes: ''
              });
            }}
            size="sm"
            className="btn-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Aggiungi Endpoint
          </Button>
        </CardHeader>
        <CardContent>
          {/* Endpoints List */}
          {apiData.endpoints.length === 0 ? (
            <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
              <Code className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nessun endpoint configurato</p>
              <p className="text-sm mt-2">Clicca "Aggiungi Endpoint" per iniziare</p>
            </div>
          ) : (
            <div className="space-y-3">
              {apiData.endpoints.map((endpoint, index) => (
                <div key={endpoint.id || index} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-mono font-bold ${
                          endpoint.method === 'GET' ? 'bg-blue-100 text-blue-700' :
                          endpoint.method === 'POST' ? 'bg-green-100 text-green-700' :
                          endpoint.method === 'PUT' ? 'bg-yellow-100 text-yellow-700' :
                          endpoint.method === 'DELETE' ? 'bg-red-100 text-red-700' :
                          'bg-purple-100 text-purple-700'
                        }`}>
                          {endpoint.method}
                        </span>
                        <code className="text-sm font-mono" style={{ color: 'var(--text-primary)' }}>
                          {endpoint.path}
                        </code>
                      </div>
                      <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {endpoint.summary}
                      </p>
                      {endpoint.parameters.length > 0 && (
                        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                          {endpoint.parameters.length} parametri configurati
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleEditEndpoint(index)}
                        size="sm"
                        variant="ghost"
                      >
                        <FileText className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleRemoveEndpoint(index)}
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Endpoint Editor Modal */}
      {showEndpointEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-white z-10 border-b">
              <CardTitle>
                {editingEndpointIndex !== null ? 'Modifica Endpoint' : 'Nuovo Endpoint'}
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowEndpointEditor(false);
                  setEditingEndpointIndex(null);
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
              <Tabs defaultValue="basic">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="basic">Base</TabsTrigger>
                  <TabsTrigger value="params">Parametri</TabsTrigger>
                  <TabsTrigger value="examples">Esempi</TabsTrigger>
                </TabsList>

                {/* Basic Tab */}
                <TabsContent value="basic" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Metodo HTTP *</label>
                      <select
                        className="w-full p-2 border rounded-md"
                        value={currentEndpoint.method}
                        onChange={(e) => setCurrentEndpoint({ ...currentEndpoint, method: e.target.value })}
                      >
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                        <option value="PUT">PUT</option>
                        <option value="PATCH">PATCH</option>
                        <option value="DELETE">DELETE</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Path *</label>
                      <Input
                        value={currentEndpoint.path}
                        onChange={(e) => setCurrentEndpoint({ ...currentEndpoint, path: e.target.value })}
                        placeholder="/api/v1/resource"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Summary *</label>
                    <Input
                      value={currentEndpoint.summary}
                      onChange={(e) => setCurrentEndpoint({ ...currentEndpoint, summary: e.target.value })}
                      placeholder="Breve descrizione dell'endpoint"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Descrizione Dettagliata</label>
                    <textarea
                      className="w-full p-2 border rounded-md"
                      rows={4}
                      value={currentEndpoint.description}
                      onChange={(e) => setCurrentEndpoint({ ...currentEndpoint, description: e.target.value })}
                      placeholder="Descrizione completa dell'endpoint, casi d'uso, note..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Codici di Errore</label>
                    <textarea
                      className="w-full p-2 border rounded-md font-mono text-sm"
                      rows={3}
                      value={currentEndpoint.error_codes}
                      onChange={(e) => setCurrentEndpoint({ ...currentEndpoint, error_codes: e.target.value })}
                      placeholder="400 - Bad Request&#10;401 - Unauthorized&#10;404 - Not Found"
                    />
                  </div>
                </TabsContent>

                {/* Parameters Tab */}
                <TabsContent value="params" className="space-y-4">
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h4 className="font-medium mb-4">Aggiungi Parametro</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        placeholder="Nome parametro"
                        value={currentParam.name}
                        onChange={(e) => setCurrentParam({ ...currentParam, name: e.target.value })}
                      />
                      <select
                        className="p-2 border rounded-md"
                        value={currentParam.type}
                        onChange={(e) => setCurrentParam({ ...currentParam, type: e.target.value })}
                      >
                        <option value="query">Query</option>
                        <option value="body">Body</option>
                        <option value="header">Header</option>
                        <option value="path">Path</option>
                      </select>
                      <select
                        className="p-2 border rounded-md"
                        value={currentParam.data_type}
                        onChange={(e) => setCurrentParam({ ...currentParam, data_type: e.target.value })}
                      >
                        <option value="string">String</option>
                        <option value="integer">Integer</option>
                        <option value="boolean">Boolean</option>
                        <option value="object">Object</option>
                        <option value="array">Array</option>
                      </select>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="param_required"
                          checked={currentParam.required}
                          onChange={(e) => setCurrentParam({ ...currentParam, required: e.target.checked })}
                        />
                        <label htmlFor="param_required" className="text-sm">Richiesto</label>
                      </div>
                      <Input
                        placeholder="Descrizione"
                        value={currentParam.description}
                        onChange={(e) => setCurrentParam({ ...currentParam, description: e.target.value })}
                      />
                      <Input
                        placeholder="Esempio"
                        value={currentParam.example}
                        onChange={(e) => setCurrentParam({ ...currentParam, example: e.target.value })}
                      />
                    </div>
                    <Button onClick={handleAddParameter} size="sm" className="mt-3">
                      <Plus className="w-4 h-4 mr-2" />
                      Aggiungi
                    </Button>
                  </div>

                  {/* Parameters List */}
                  {currentEndpoint.parameters.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium">Parametri Configurati</h4>
                      {currentEndpoint.parameters.map((param) => (
                        <div key={param.id} className="border rounded p-3 flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <code className="text-sm font-mono font-semibold">{param.name}</code>
                              <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                                {param.type}
                              </span>
                              <span className="text-xs px-2 py-0.5 rounded bg-gray-100">
                                {param.data_type}
                              </span>
                              {param.required && (
                                <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">
                                  required
                                </span>
                              )}
                            </div>
                            {param.description && (
                              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                                {param.description}
                              </p>
                            )}
                            {param.example && (
                              <p className="text-xs mt-1 font-mono" style={{ color: 'var(--text-muted)' }}>
                                Esempio: {param.example}
                              </p>
                            )}
                          </div>
                          <Button
                            onClick={() => handleRemoveParameter(param.id)}
                            size="sm"
                            variant="ghost"
                            className="text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Examples Tab */}
                <TabsContent value="examples" className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Esempio Richiesta (JSON)</label>
                    <textarea
                      className="w-full p-3 border rounded-md font-mono text-sm bg-gray-50"
                      rows={8}
                      value={currentEndpoint.request_example}
                      onChange={(e) => setCurrentEndpoint({ ...currentEndpoint, request_example: e.target.value })}
                      placeholder='{\n  "field": "value",\n  "array": [1, 2, 3]\n}'
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Esempio Risposta (JSON)</label>
                    <textarea
                      className="w-full p-3 border rounded-md font-mono text-sm bg-gray-50"
                      rows={8}
                      value={currentEndpoint.response_example}
                      onChange={(e) => setCurrentEndpoint({ ...currentEndpoint, response_example: e.target.value })}
                      placeholder='{\n  "success": true,\n  "data": {...}\n}'
                    />
                  </div>
                </TabsContent>
              </Tabs>

              <div className="flex gap-2 pt-4 border-t">
                <Button onClick={handleAddEndpoint} className="btn-primary flex-1">
                  {editingEndpointIndex !== null ? 'Aggiorna Endpoint' : 'Aggiungi Endpoint'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEndpointEditor(false);
                    setEditingEndpointIndex(null);
                  }}
                >
                  Annulla
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Save Actions */}
      <div className="flex gap-4 justify-end sticky bottom-0 bg-white py-4 border-t">
        <Button variant="outline" onClick={onCancel} disabled={isLoading}>
          Annulla
        </Button>
        <Button onClick={handleSaveAPI} className="btn-primary" disabled={isLoading || !apiData.name || !apiData.description}>
          {isLoading ? 'Salvataggio...' : 'Salva API'}
        </Button>
      </div>

      {/* Validation Warning */}
      {(!apiData.name || !apiData.description) && (
        <div className="fixed bottom-20 right-8 max-w-sm">
          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded shadow-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Campi Obbligatori</p>
                <p className="text-sm text-amber-700 mt-1">
                  Nome e Descrizione API sono richiesti per salvare
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default APIBuilder;
